import React, { useEffect, useState } from 'react';
import MediaPicker from '../../components/admin/MediaPicker';
import { resolveSeoTitle } from '../../context/SiteSettingsContext';
import { apiDelete, apiGet, apiPost, apiPut } from '../../api/client';
import { AiStatusCard, BackupCard, MediaHealthCard } from '../../components/admin/SiteSafetyPanel';

const EMPTY_USER = { username: '', password: '' };

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState(EMPTY_USER);
  const [seoForm, setSeoForm] = useState(null);
  const [maintenanceForm, setMaintenanceForm] = useState(null);
  const [message, setMessage] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [savingSeo, setSavingSeo] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    const [siteSettings, products, reviews, schedule, toolPages, streamers, userList] = await Promise.all([
      apiGet('/admin/settings'),
      apiGet('/products'),
      apiGet('/reviews'),
      apiGet('/schedule'),
      apiGet('/admin/tool-pages'),
      apiGet('/streamers?raw=1').catch(() => []),
      apiGet('/admin/users'),
    ]);
    setSettings(siteSettings);
    setSeoForm({
      seoTitleTemplate: siteSettings.seo?.titleTemplate || '',
      seoDefaultTitle: siteSettings.seo?.defaultTitle || '',
      seoDefaultDescription: siteSettings.seo?.defaultDescription || '',
      seoDefaultKeywords: siteSettings.seo?.defaultKeywords || '',
      seoDefaultOgImage: siteSettings.seo?.defaultOgImage || '',
    });
    setMaintenanceForm({
      maintenanceEnabled: Boolean(siteSettings.maintenance?.enabled),
      maintenanceMessage: siteSettings.maintenance?.message || '',
    });
    setOverview({
      tools: products.length,
      reviews: reviews.length,
      schedule: schedule.length,
      toolPages: toolPages.length,
      streamers: Array.isArray(streamers) ? streamers.length : 0,
    });
    setUsers(userList);
  };

  useEffect(() => {
    loadAll()
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const syncYouTube = async () => {
    setSyncing(true);
    setMessage('');
    try {
      const result = await apiPost('/videos/sync');
      const refreshed = await apiGet('/admin/settings');
      setSettings(refreshed);
      setMessage(`Synced ${result.count} videos from YouTube.`);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const saveSeo = async () => {
    setSavingSeo(true);
    setMessage('');
    try {
      const updated = await apiPut('/admin/settings', seoForm);
      setSettings(updated);
      setMessage('SEO settings saved.');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSavingSeo(false);
    }
  };

  const saveMaintenance = async () => {
    setSavingMaintenance(true);
    setMessage('');
    try {
      const updated = await apiPut('/admin/settings', maintenanceForm);
      setSettings(updated);
      setMessage('Maintenance settings saved.');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSavingMaintenance(false);
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      await apiPost('/admin/users', userForm);
      setUserForm(EMPTY_USER);
      setUsers(await apiGet('/admin/users'));
      setMessage('Admin user created.');
    } catch (err) {
      setMessage(err.message);
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm('Remove this admin account?')) return;
    setMessage('');
    try {
      await apiDelete(`/admin/users/${id}`);
      setUsers(await apiGet('/admin/users'));
      setMessage('Admin user removed.');
    } catch (err) {
      setMessage(err.message);
    }
  };

  const lastSyncLabel = settings?.youtubeLastSync
    ? new Date(settings.youtubeLastSync).toLocaleString()
    : 'Never';

  const seoPreview = seoForm
    ? resolveSeoTitle('Live Stream', seoForm.seoTitleTemplate, seoForm.seoDefaultTitle)
    : '';

  const syncHealthLabel = {
    ok: 'Healthy',
    stale: 'Stale (over 4h)',
    error: 'Last sync failed',
    unknown: 'Unknown',
  }[settings?.youtubeSyncHealth || 'unknown'];

  return (
    <div className="admin-panel-page">
      <div className="admin-settings-grid">
        <div className="admin-settings-card admin-settings-card--wide">
          <h3>Site SEO defaults</h3>
          <p>Applied site-wide when pages do not override title, description, or OG image.</p>
          {loading || !seoForm ? (
            <p>Loading…</p>
          ) : (
            <div className="admin-form admin-settings-form">
              <label>
                Title template (<code>{'{title}'}</code> placeholder)
                <input
                  value={seoForm.seoTitleTemplate}
                  onChange={(e) => setSeoForm({ ...seoForm, seoTitleTemplate: e.target.value })}
                />
              </label>
              <label>
                Default title (when page has no title)
                <input
                  value={seoForm.seoDefaultTitle}
                  onChange={(e) => setSeoForm({ ...seoForm, seoDefaultTitle: e.target.value })}
                />
              </label>
              <p className="admin-settings-preview">
                Preview: <code>{seoPreview}</code>
              </p>
              <label>
                Default description
                <textarea
                  rows={3}
                  value={seoForm.seoDefaultDescription}
                  onChange={(e) => setSeoForm({ ...seoForm, seoDefaultDescription: e.target.value })}
                />
              </label>
              <label>
                Default keywords
                <input
                  value={seoForm.seoDefaultKeywords}
                  onChange={(e) => setSeoForm({ ...seoForm, seoDefaultKeywords: e.target.value })}
                />
              </label>
              <MediaPicker
                label="Default OG image"
                value={seoForm.seoDefaultOgImage}
                onChange={(url) => setSeoForm({ ...seoForm, seoDefaultOgImage: url })}
                placeholder="https://zemaxi.com/images/og-image.png"
              />
              <div className="admin-settings-actions">
                <button type="button" className="btn btn-primary" onClick={saveSeo} disabled={savingSeo}>
                  {savingSeo ? 'Saving…' : 'Save SEO settings'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="admin-settings-card admin-settings-card--wide">
          <h3>Maintenance mode</h3>
          <p>When enabled, visitors see a themed offline page. Admin routes stay accessible.</p>
          {loading || !maintenanceForm ? (
            <p>Loading…</p>
          ) : (
            <div className="admin-form admin-settings-form">
              <label className="admin-checkbox-row">
                <input
                  type="checkbox"
                  checked={maintenanceForm.maintenanceEnabled}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, maintenanceEnabled: e.target.checked })}
                />
                Enable maintenance mode
              </label>
              <label>
                Custom message
                <textarea
                  rows={4}
                  value={maintenanceForm.maintenanceMessage}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, maintenanceMessage: e.target.value })}
                />
              </label>
              <div className="admin-settings-actions">
                <button type="button" className="btn btn-primary" onClick={saveMaintenance} disabled={savingMaintenance}>
                  {savingMaintenance ? 'Saving…' : 'Save maintenance settings'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="admin-settings-card">
          <h3>YouTube sync</h3>
          <p>Pulls videos from your channel into the site database. Used on the Videos page and home page.</p>
          {loading ? (
            <p>Loading…</p>
          ) : (
            <>
              <div className="admin-stat-chips" style={{ marginTop: '0.5rem' }}>
                <div className="admin-stat-chip">
                  <span className="admin-stat-value">{settings?.videoCount ?? 0}</span>
                  <span className="admin-stat-label">In DB</span>
                </div>
              </div>
              <p className="admin-status-row">
                <span className={`admin-status-dot admin-status-dot--${settings?.youtubeSyncHealth || 'unknown'}`} />
                Cron health: {syncHealthLabel}
              </p>
              <p className="admin-status-row">
                <span className={`admin-status-dot ${settings?.youtubeApiConfigured ? 'is-ok' : 'is-warn'}`} />
                API key {settings?.youtubeApiConfigured ? 'configured' : 'missing in api/.env'}
              </p>
              <p><strong>Last sync:</strong> {lastSyncLabel}</p>
              {settings?.youtubeLastSyncSource && (
                <p><strong>Source:</strong> {settings.youtubeLastSyncSource}</p>
              )}
              {settings?.youtubeLastSyncError && (
                <p className="admin-message admin-message--error">{settings.youtubeLastSyncError}</p>
              )}
              <p className="admin-status-row">
                <span className={`admin-status-dot ${settings?.twitchApiConfigured ? 'is-ok' : 'is-warn'}`} />
                Twitch API {settings?.twitchApiConfigured ? 'configured' : 'missing in api/.env'}
              </p>
              <p><strong>Channel:</strong> <code>{settings?.youtubeChannelId || '—'}</code></p>
              <div className="admin-settings-actions">
                <button type="button" className="btn btn-primary" onClick={syncYouTube} disabled={syncing || !settings?.youtubeApiConfigured}>
                  {syncing ? 'Syncing…' : 'Sync YouTube now'}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="admin-settings-card">
          <h3>Admin users</h3>
          <p>Manage CMS login accounts.</p>
          {loading ? (
            <p>Loading…</p>
          ) : (
            <>
              <ul className="admin-user-list">
                {users.map((u) => (
                  <li key={u.id}>
                    <span>{u.username}</span>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeUser(u.id)}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
              <form className="admin-form admin-settings-form" onSubmit={addUser}>
                <label>
                  Username
                  <input
                    value={userForm.username}
                    onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                    required
                  />
                </label>
                <label>
                  Password (min 8 characters)
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    minLength={8}
                    required
                  />
                </label>
                <button type="submit" className="btn btn-primary">Add admin user</button>
              </form>
            </>
          )}
        </div>

        <BackupCard />
        <MediaHealthCard />
        <AiStatusCard />

        <div className="admin-settings-card">
          <h3>Content overview</h3>
          <p>Live counts from the CMS database.</p>
          {loading ? (
            <p>Loading…</p>
          ) : (
            <div className="admin-stat-chips" style={{ marginTop: '0.5rem' }}>
              <div className="admin-stat-chip">
                <span className="admin-stat-value">{overview?.tools ?? 0}</span>
                <span className="admin-stat-label">Tools</span>
              </div>
              <div className="admin-stat-chip admin-stat-chip--purple">
                <span className="admin-stat-value">{overview?.reviews ?? 0}</span>
                <span className="admin-stat-label">Reviews</span>
              </div>
              <div className="admin-stat-chip admin-stat-chip--green">
                <span className="admin-stat-value">{overview?.schedule ?? 0}</span>
                <span className="admin-stat-label">Schedule</span>
              </div>
              <div className="admin-stat-chip admin-stat-chip--gold">
                <span className="admin-stat-value">{overview?.toolPages ?? 0}</span>
                <span className="admin-stat-label">Tool pages</span>
              </div>
              <div className="admin-stat-chip admin-stat-chip--purple">
                <span className="admin-stat-value">{overview?.streamers ?? 0}</span>
                <span className="admin-stat-label">Streamers</span>
              </div>
            </div>
          )}
          <p style={{ marginTop: '0.75rem' }}>
            Content updates appear on the site immediately — no React rebuild required.
          </p>
        </div>

        <div className="admin-settings-card admin-settings-card--wide">
          <h3>Production cron</h3>
          <p>On cPanel (or your host), schedule YouTube sync every 3 hours:</p>
          <code className="admin-code">php /home/u923803459/domains/zemaxi.com/public_html/api/cron/sync_youtube.php</code>
          <p style={{ marginTop: '0.65rem' }}>
            Set <code>YOUTUBE_API_KEY</code> in <code>api/.env</code> on the server. Local Docker uses the same file.
          </p>
          <p>
            <strong>API URL:</strong> <code>{process.env.REACT_APP_API_URL || '/api'}</code>
            {process.env.NODE_ENV === 'development' && ' (development)'}
          </p>
        </div>
      </div>

      {message && <p className="admin-message admin-panel-message">{message}</p>}
    </div>
  );
}
